import CloseIcon from '@mui/icons-material/Close';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,IconButton,Stack,TextField,Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../../helpers/attToken';
import { trackEventMatomo } from '../../../helpers/matomo';
import PlanoDeAcao from '../../../service/PlanoDeAcao';
import { planoDeAcaoType } from '../../../types/plano-de-acao/types';
import queryClient from '../../../service/query';
import AccordionDetailPlan from './DetalhePlano';

type props = {
  openModal: boolean,
  setOpenModal: Function,
  plano: planoDeAcaoType,
  setSelectedPlano: Function,
  tab: 'pendentes' | 'finalizadas'
};

const PlanoDeAcaoService = new PlanoDeAcao();

type avaliacaoOptions = "pendente" | "malSucedido" | "bemSucedido";

export default function ModalAvalia({ openModal, setOpenModal, plano, setSelectedPlano, tab }: props) {
  const [avaliacao, setAvalicao] = useState(tab === 'pendentes' ? 'pendente' : plano.avaliacao || 'pendente');
  const [detalhes, setDetalhes] = useState(tab === 'pendentes' ? '' : plano.detalhesAvaliacao || '');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('translation');

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openModal}
      onClose={() => setOpenModal(false)}
      scroll='paper'
    >
      <DialogTitle>{tab !== 'pendentes'
        ? `${t('ActionPlan.Modal.planEvaluationDetails')} "${plano.nome}"`
        : `${t('ActionPlan.Modal.evaluatePlanEffectiveness')} "${plano.nome}"`}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setOpenModal(false)}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' width='100%' spacing={2}>
          <Typography color='textDisabled' fontWeight={600} variant='h6' style={{ wordBreak: 'break-all' }}>{`${t('ActionPlan.Modal.planWas')} "${plano.nome}" ${t('ActionPlan.Modal.was')}`}</Typography>
          <Stack direction='row' justifyContent='space-around' alignItems='center' width='100%'>
            <label className='radio' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: tab !== 'pendentes' ? 'not-allowed' : 'pointer' }}>
              <input
                type="radio"
                name="answer"
                checked={avaliacao === "malSucedido"}
                value="malSucedido"
                onChange={(e) => {
                  trackEventMatomo('analise PA', 'click', 'radio', 'seleciona valor');
                  setAvalicao(e.target.value as avaliacaoOptions);
                }}
                readOnly
                disabled={tab !== 'pendentes'}
              />
              &nbsp;<span className='tag is-danger'>{t('ActionPlan.Modal.unsuccessful')}</span>
            </label>
            <label className='radio' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: tab !== 'pendentes' ? 'not-allowed' : 'pointer' }}>
              <input
                type="radio"
                name="answer"
                checked={avaliacao === "bemSucedido"}
                value="bemSucedido"
                onChange={(e) => {
                  trackEventMatomo('analise PA', 'click', 'radio', 'seleciona valor');
                  setAvalicao(e.target.value as avaliacaoOptions);
                }}
                readOnly
                disabled={tab !== 'pendentes'}
              />
              &nbsp;<span className='tag is-success'>{t('ActionPlan.Modal.successful')}</span>
            </label>
          </Stack>
          {tab === 'pendentes' &&
            <>
              <Typography color='textDisabled' fontWeight={600}>{t('ActionPlan.Modal.feelFreeToDetail')}</Typography>
              <TextField
                multiline
                fullWidth
                label={t('ActionPlan.Modal.planDetails')}
                rows={4}
                maxRows={4}
                value={detalhes}
                onClick={() => trackEventMatomo('analise PA', 'click', 'textarea', 'edita valor')}
                onChange={(e) => setDetalhes(e.target.value)}
              />
            </>}
          <AccordionDetailPlan plano={plano} />
        </Stack>
      </DialogContent>
      {tab === 'pendentes'
        && <DialogActions>
          <Button
            className={`button ${avaliacao === 'pendente' ? 'is-warning' : avaliacao === 'malSucedido' ? 'is-danger' : 'is-success'}`}
            disabled={loading || avaliacao === 'pendente'}
            onClick={async () => {
              trackEventMatomo('analise PA', 'click', 'button', 'avalia PA');
              setLoading(true);
              const token = await AttToken();
              if (token) {
                await toast.promise(
                  PlanoDeAcaoService.evaluatePlan(token, plano.id as string, avaliacao, detalhes),
                  {
                    pending: t('ActionPlan.Modal.evaluatingPlan'),
                    success: t('ActionPlan.Modal.planEvaluated'),
                    error: t('ActionPlan.Modal.failedToChangeStatus')
                  }
                );
              }
              queryClient.resetQueries({ queryKey: ['GET_HISTORICO'] });
              setLoading(false);
              setOpenModal(!openModal);
              setSelectedPlano(undefined);
            }}
          >
            {avaliacao === 'pendente'
              ? `${t('ActionPlan.Modal.selectEffectiveness')}`
              : `${t('ActionPlan.Modal.evaluateAs')} ${avaliacao === 'malSucedido'
                ? `${t('ActionPlan.Modal.unsuccessful')}`
                : `${t('ActionPlan.Modal.successful')}`}`}
          </Button>
        </DialogActions>}
    </Dialog>
  );
}
